import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import styles from "./MultiComboBox.module.less";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	generateTrackingId,
	setTrackingId,
	ensureNotNil
} from "client/Misc/utils";
import {
	useKeyboardListNavigation,
	useResizeObserver,
	useSynchronizedRef
} from "client/Hooks";
import { Tag, TagColor } from "../Tag/Tag";
import { Key } from "client/Misc/commonTypes";
import { AutogrowTextInput, AutogrowTextInputHandle } from "./AutogrowTextInput";
import classNames from "classnames";
import { KeyEventArguments } from "../TextInput/TextInput";
import { Highlight } from "../HighlightedText/HighlightedText";
import {
	BaseSuggestionBoxProps,
	MultiComboBoxSuggestionBoxHandle,
	MultiComboBoxSuggestionsResult,
	MultiComboBoxSuggestionBoxProps,
	getItemValueTrackingId,
	SuggestionBox
} from "./SuggestionBox";
import { FontIconName } from "../Icon/Icon";

type MultiComboBoxProps<TValue> = {
	value: readonly MultiComboBoxItem<TValue>[];
	onChange: (value: readonly MultiComboBoxItem<TValue>[]) => void;
	onFind: (
		searchTerm: string,
		signal: AbortSignal
	) => Promise<void>;
	placeholder?: string;
	isInvalid?: boolean;
	trackingId: string;
	renderSuggestionBox: (
		props: BaseSuggestionBoxProps<TValue>,
		ref: Ref<MultiComboBoxSuggestionBoxHandle<TValue>>
	) => ReactElement;
};

export const MultiComboBox = <TValue,>(props: MultiComboBoxProps<TValue>): ReactElement => {
	const {
		value,
		onChange,
		onFind,
		placeholder,
		isInvalid,
		trackingId,
		renderSuggestionBox
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [areSuggestionsOpen, setAreSuggestionsOpen] = useState<boolean>(false);
	const [hasFocus, setHasFocus] = useState<boolean>(false);

	const findCancellation: RefObject<AbortController | null> = useRef(null);
	const inputRef: RefObject<AutogrowTextInputHandle | null> = useRef(null);
	const ref: RefObject<HTMLDivElement | null> = useRef(null);
	const ignoreFocusEventRef: RefObject<boolean> = useRef<boolean>(false);
	const prevSearchTermRef: RefObject<string | null> = useRef<string>(null);
	const suggestionBoxRef: RefObject<MultiComboBoxSuggestionBoxHandle<TValue> | null> = useRef<MultiComboBoxSuggestionBoxHandle<TValue>>(null);

	const { width } = useResizeObserver(ref);

	const doSearchHandler = useCallback(
		async (searchTerm: string): Promise<void> => {
			findCancellation.current?.abort();
			const cancellation = new AbortController();
			findCancellation.current = cancellation;

			try {
				prevSearchTermRef.current = searchTerm;

				await onFind(
					searchTerm,
					cancellation.signal
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[onFind]
	);

	const doSearchWhenNeededHandler = useCallback(
		(newSearchTerm: string): void => {
			if (newSearchTerm !== prevSearchTermRef.current) {
				doSearchHandler(newSearchTerm);
			}
		},
		[
			doSearchHandler
		]
	);

	const textInputChangeHandler = useCallback(
		(newSearchTerm: string): void => {
			setSearchTerm(newSearchTerm);
			setAreSuggestionsOpen(true);
			doSearchWhenNeededHandler(newSearchTerm);
		},
		[doSearchWhenNeededHandler]
	);

	const textInputFocusHandler = useCallback(
		(): void => {
			if (ignoreFocusEventRef.current) {
				return;
			}

			setAreSuggestionsOpen(true);
			doSearchWhenNeededHandler(searchTerm);
		},
		[doSearchWhenNeededHandler, searchTerm]
	);

	const resetSearchTerm = useCallback(
		(options: { shouldCloseSuggestions: boolean; }): void => {
			setSearchTerm("");

			if (options.shouldCloseSuggestions) {
				findCancellation.current?.abort();

				setAreSuggestionsOpen(false);
			} else {
				doSearchWhenNeededHandler("");
			}
		},
		[doSearchWhenNeededHandler]
	);

	const focusInputHandler = useCallback(
		(): void => {
			inputRef.current
				?.focus();
		},
		[]
	);

	const focusInputHandlerWithoutChangingSuggestionState = useCallback(
		(): void => {
			ignoreFocusEventRef.current = true;

			inputRef.current
				?.focus();

			ignoreFocusEventRef.current = false;
		},
		[]
	);

	const removeItemAtIndex = (index: number): void => {
		onChange(value.filter((_, i) => i !== index));
		focusInputHandlerWithoutChangingSuggestionState();
	};

	const addValueHandler = useCallback(
		(newValue: MultiComboBoxItem<TValue>): void => {
			onChange([...value, newValue]);
			resetSearchTerm({ shouldCloseSuggestions: true });
			focusInputHandlerWithoutChangingSuggestionState();
		},
		[
			focusInputHandlerWithoutChangingSuggestionState,
			onChange,
			resetSearchTerm,
			value
		]
	);

	const {
		contextData: keyboardContextDate,
		focusFirstItemHandler
	} = useKeyboardListNavigation({
		onFocusInput: focusInputHandlerWithoutChangingSuggestionState,
		onCancel: () => resetSearchTerm({ shouldCloseSuggestions: true }),
		shouldCancelDirectlyFromItems: true
	});

	const selectFirstSuggestedItem = useCallback(
		(): boolean => {
			const topSuggestion: MultiComboBoxItem<TValue> | undefined = ensureNotNil(
				suggestionBoxRef.current,
				"Suggestion box ref is not set"
			)
				.getTopSuggestion();

			if (topSuggestion) {
				addValueHandler(topSuggestion);
				return true;
			}

			return false;
		},
		[addValueHandler]
	);

	const searchKeyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			switch (args.key) {
				case Key.Enter:
					if (areSuggestionsOpen) {
						return selectFirstSuggestedItem();
					}
					return false;
				case Key.ArrowDown:
					focusFirstItemHandler();
					return true;
				case Key.Escape:
					resetSearchTerm({ shouldCloseSuggestions: true });
					return true;
				case Key.Backspace:
					if (searchTerm === "" && value.length > 0) {
						onChange(
							value.slice(0, value.length - 1)
						);
						return true;
					}
					return false;
				default:
					return false;
			}
		},
		[
			areSuggestionsOpen,
			focusFirstItemHandler,
			resetSearchTerm,
			searchTerm,
			value,
			onChange,
			selectFirstSuggestedItem
		]
	);

	const hasFocusRef: ReadonlyRefObject<boolean> = useSynchronizedRef(hasFocus);

	const blurHandler = useCallback(
		(): void => {
			setHasFocus(false);

			// We cannot directly reset search as shortly after something else in the component
			// can gain focus (i.e. focusout (onBlur) fires first and then focusin (onFocus))
			setTimeout(
				(): void => {
					if (!hasFocusRef.current) {
						resetSearchTerm({ shouldCloseSuggestions: true });
					}
				},
				0
			);
		},
		[
			hasFocusRef,
			resetSearchTerm
		]
	);

	const trackingIdCounters = new Map<string, number>();
	const getItemKey = (trackingId: string): string => {
		const occurrence: number = (trackingIdCounters.get(trackingId) ?? 0) + 1;

		trackingIdCounters.set(
			trackingId,
			occurrence
		);

		let result: string = trackingId;

		if (occurrence > 1) {
			result += `-${occurrence}`;
		}

		return result;
	};

	const isPlaceholderVisible: boolean = value.length === 0
		&& Boolean(placeholder)
		&& !searchTerm;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{
					[styles.root_focus]: hasFocus,
					[styles.root_isInvalid]: isInvalid
				}
			)}
			onClick={() => focusInputHandler()}
			ref={ref}
			onFocus={() => setHasFocus(true)}
			onBlur={blurHandler}
		>
			<div className={styles.content}>
				{isPlaceholderVisible && (
					<span className={styles.placeholder}>
						{placeholder}
					</span>
				)}

				{value.map((x, i): ReactElement => {
					const itemTrackingId: string = getItemValueTrackingId(x);
					const key: string = getItemKey(itemTrackingId);
					return (
						<span
							key={key}
							className={styles.value}
						>
							<Tag
								trackingId={generateTrackingId("value", `|${itemTrackingId}`)}
								text={x.text}
								onDelete={() => removeItemAtIndex(i)}
								isDeleteFocusable={false}
								icon={x.icon}
								color={x.color}
							/>
						</span>
					);
				})}

				<AutogrowTextInput
					value={searchTerm}
					onChange={textInputChangeHandler}
					isBorderless
					height="fill"
					ref={inputRef}
					onKeyDown={searchKeyDownHandler}
					trackingId={generateTrackingId(trackingId, "__input")}
					onFocus={textInputFocusHandler}
					hasNoPadding
				/>

				{areSuggestionsOpen
					&& ref.current
					&& renderSuggestionBox(
						{
							target: ref.current,
							onSelectItem: addValueHandler,
							keyboardNavigationContext: keyboardContextDate,
							targetWidth: width,
							searchTerm,
							trackingId: generateTrackingId(trackingId, "__dropover__wrapper"),
							onClose: () => resetSearchTerm({ shouldCloseSuggestions: true }),
							onRetrySearch: () => doSearchHandler(searchTerm)
						},
						suggestionBoxRef
					)
				}
			</div>
		</div>
	);
};
MultiComboBox.displayName = "MultiComboBox";

MultiComboBox.SuggestionBox = SuggestionBox;

export type MultiComboBoxItemGroup = {
	name: string;
	title?: string;
};

export type MultiComboBoxItem<TValue> = {
	text: string;
	optionPrefix?: string;
	value: TValue;
	valueTrackingId?: string;
	group?: MultiComboBoxItemGroup;
	icon?: FontIconName;
	color?: TagColor;
};

export type SuggestedMultiComboBoxItem<TValue, TRenderData = undefined> = MultiComboBoxItem<TValue>
	& {
		highlight?: Highlight | nil;
	}
	& ([TRenderData] extends [undefined]
		? {
			data?: TRenderData;
		}
		: {
			data: TRenderData;
		}
	);

export type {
	MultiComboBoxSuggestionsResult,
	MultiComboBoxSuggestionBoxProps,
	MultiComboBoxSuggestionBoxHandle
};